import type { BehaviorType, YMap } from '@yandex/ymaps3-types';
import classNames from 'classnames';
import { useInView } from 'framer-motion';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useLocale } from '@/shared/hooks/use-locale';
import { tp } from '@/shared/lib/typograf';
import loadScript, { isScriptLoaded } from '@/shared/lib/utils/load-script';
import Loader from '../Loader';
import styles from './customization.json';

interface Props extends React.HTMLAttributes<HTMLElement> {
    centerCoords: [number, number];
    centerMarkerText: string;
    zoom: number;
}

const API_KEY = 'c6862c10-2fdc-4c5e-969d-b5a59b65015b';

const Map = forwardRef<HTMLDivElement, Props>(({ centerCoords, centerMarkerText, zoom, ...props }, ref) => {
    const el = useRef<HTMLDivElement>(null);
    const inView = useInView(el, { once: true });
    const map = useRef<YMap | null>(null);
    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const locale = useLocale();
    const lang = locale === 'en' ? 'en_GB' : 'ru_RU';
    const url = `https://api-maps.yandex.ru/v3/?apikey=${API_KEY}&lang=${lang}`;

    useEffect(() => {
        const _el = el.current;

        if (inView) {
            const initMap = async () => {
                setReady(true);

                if (el.current) {
                    const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer } = window.ymaps3;
                    const ymap: YMap = new YMap(
                        el.current,
                        {
                            location: {
                                center: centerCoords,
                                zoom: zoom,
                            },
                            behaviors: ['drag', 'pinchZoom', 'mouseTilt', 'scrollZoom'],
                        },
                        [],
                    );

                    map.current = ymap;
                    ymap.addChild(
                        new YMapDefaultSchemeLayer({
                            customization: styles,
                        }),
                    ).addChild(new YMapDefaultFeaturesLayer({ zIndex: 1800 }));

                    const { YMapGeolocationControl, YMapZoomControl } = await ymaps3.import(
                        '@yandex/ymaps3-controls@0.0.1',
                    );

                    ymap.addChild(
                        new ymaps3.YMapControls({ position: 'right' }, [
                            new YMapZoomControl({}),
                            new YMapGeolocationControl({}),
                        ]),
                    );
                }
            };

            if (isScriptLoaded(url)) {
                setLoading(true);
                initMap()
                    .catch((err) => {
                        setErrorMessage(tp(err instanceof Error ? err.message : '', locale));
                        throw err;
                    })
                    .finally(() => {
                        setTimeout(() => {
                            setLoading(false);
                        }, 1000);
                    });
            } else {
                setLoading(true);
                loadScript(url)
                    .then(() => window.ymaps3.ready)
                    .then(initMap)
                    .catch((err) => {
                        setErrorMessage(tp(err instanceof Error ? err.message : '', locale));
                        throw err;
                    })
                    .finally(() => {
                        setTimeout(() => {
                            setLoading(false);
                        }, 1000);
                    });
            }
        }

        return () => {
            if (map.current) {
                map.current.destroy();
                map.current = null;
            }
        };
    }, [centerCoords, inView, locale, zoom, url]);

    useEffect(() => {
        const { current } = el;

        if (ready) {
            const defaultBehaviors: BehaviorType[] = ['drag', 'pinchZoom', 'mouseTilt', 'scrollZoom'];
            map.current?.setBehaviors(defaultBehaviors);

            const enableScroll = () => {
                map.current?.setBehaviors(['scrollZoom', ...defaultBehaviors]);
            };

            const disableScroll = () => {
                map.current?.setBehaviors(defaultBehaviors);
            };

            current?.addEventListener('click', enableScroll);
            current?.addEventListener('mouseleave', disableScroll);

            return () => {
                current?.removeEventListener('click', enableScroll);
                current?.removeEventListener('mouseleave', disableScroll);
            };
        }
    }, [ready]);

    useEffect(() => {
        let marker: any;
        let markerEl: HTMLElement | null;
        const { current } = map;

        if (ready && window.ymaps3) {
            const { YMapMarker } = window.ymaps3;
            markerEl = document.createElement('div');
            markerEl.innerHTML = `
                <div class="map-marker">
                    <div class="map-marker__inner">
                        <span class="map-marker__text text-xs">${centerMarkerText}</span>
                    </div>
                </div>
            `;
            marker = new YMapMarker({ coordinates: centerCoords }, markerEl);
            current?.addChild(marker);
        }

        return () => {
            current?.removeChild(marker);
        };
    }, [ready, centerCoords, centerMarkerText]);

    return (
        <div ref={ref} className={classNames('map', props.className)}>
            <div ref={el} className="map-inner">
                <Loader isFetching={loading} noAnimationOnStart />
            </div>
        </div>
    );
});

Map.displayName = 'Map';

export default Map;
